import React from 'react'
import { useDroppable } from '@dnd-kit/core'
import { Activation, ActivationStatus } from '../../types/activation'
import ActivationCard from './ActivationCard'

interface ActivationKanbanColumnProps {
  status: ActivationStatus
  activations: Activation[]
  onEditActivation: (activation: Activation) => void
  onDeleteActivation: (activation: Activation) => void
  onDuplicate?: (activation: Activation) => void
  onPartnerClick?: (partner: { id: string; name: string; logo_url?: string | null }) => void
  onViewInsights?: (activation: Activation) => void
}

const statusConfig: Record<ActivationStatus, { label: string; color: string; bgColor: string; borderColor: string }> = {
  Pending: {
    label: 'Pending',
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200'
  },
  Active: {
    label: 'Active',
    color: 'text-green-700',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200'
  },
  Completed: {
    label: 'Completed',
    color: 'text-blue-700',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200'
  },
  Inactive: {
    label: 'Inactive',
    color: 'text-gray-700',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200'
  },
  Suspended: {
    label: 'Suspended',
    color: 'text-red-700',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200'
  },
  Deleted: {
    label: 'Archived',
    color: 'text-gray-500',
    bgColor: 'bg-gray-100',
    borderColor: 'border-gray-300'
  }
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value)
}

const ActivationKanbanColumn: React.FC<ActivationKanbanColumnProps> = ({
  status,
  activations,
  onEditActivation,
  onDeleteActivation,
  onDuplicate,
  onPartnerClick,
  onViewInsights
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id: status,
    data: { status }
  })

  const config = statusConfig[status]
  // Use emv_target_value for lane totals (consistent with dashboard), fallback to deal_value
  const totalTargetValue = activations.reduce((sum, a) => sum + (Number(a.emv_target_value) || Number(a.deal_value) || 0), 0)

  return (
    <div
      ref={setNodeRef}
      className={`flex flex-col flex-1 min-w-[200px] rounded-lg border ${config.borderColor} ${
        isOver ? 'ring-2 ring-blue-500 ring-opacity-50' : ''
      }`}
    >
      {/* Column Header */}
      <div className={`${config.bgColor} px-3 py-2 rounded-t-lg border-b ${config.borderColor}`}>
        <div className="flex items-center justify-between">
          <h3 className={`text-sm font-semibold ${config.color}`}>
            {config.label} ({activations.length})
          </h3>
          {totalTargetValue > 0 && (
            <span className={`text-sm font-semibold ${config.color}`}>
              {formatCurrency(totalTargetValue)}
            </span>
          )}
        </div>
      </div>

      {/* Cards Container */}
      <div
        className={`flex-1 p-2 space-y-2 overflow-y-auto max-h-[calc(100vh-400px)] min-h-[200px] ${
          isOver ? config.bgColor : 'bg-gray-50/50'
        } transition-colors duration-200`}
      >
        {activations.length === 0 ? (
          <div className="flex items-center justify-center h-20 text-xs text-gray-400 italic">
            No activations
          </div>
        ) : (
          activations.map((activation) => (
            <ActivationCard
              key={activation.id}
              activation={activation}
              onEdit={onEditActivation}
              onDelete={onDeleteActivation}
              onDuplicate={onDuplicate}
              onPartnerClick={onPartnerClick}
              onViewInsights={onViewInsights}
            />
          ))
        )}
      </div>
    </div>
  )
}

export default ActivationKanbanColumn
